import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── The tax ledgers that hang off the statutory books ───────────────────────────────────────────
// Five things that had no screen at all: which way the office files, the year-end foreign-currency
// retranslation, ภ.ง.ด.54 / ภ.พ.36, stamp duty, the loss carry-forward register, and the s.105
// receipt check.
//
// Every one of them was built from a source that gives some of what it needs and not the rest, and
// the screen SAYS which. Amber here never means "probably fine" — it means a figure the Revenue
// Department would ask for is not established, and the panel below it will refuse until it is.
// Hiding that would turn a known gap into an invisible one.
//
// Operator labels run through t('pa_tax_…'). Thai that is the NAME of a statutory thing
// (ภ.ง.ด.54, ภ.พ.36, ใบรับ, ลักษณะแห่งตราสาร, อ.ส., ท.ป.4/2528) stays Thai in every locale; so do
// every figure, form code, section number and date inside the explanatory sentences. Strings the
// server returns (d.warnings, reg.caveat, thr.rule, x.expiredWarning, r.why, account names …) are
// data and are never wrapped in t().

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (s: any) => (Number(s || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toSatang = (v: string) => Math.round((Number(v) || 0) * 100);

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const row: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 9 };
const amber: React.CSSProperties = {
  fontSize: 11.5, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8',
  padding: '7px 10px', borderRadius: 8, lineHeight: 1.6,
};
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '5px 8px', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 12, color: 'var(--text-primary)', padding: '5px 8px', borderTop: '1px solid var(--border)' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function Section({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '10px 13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{subtitle}</span>
        </span>
      </button>
      {open && <div style={{ padding: '2px 13px 13px' }}>{children}</div>}
    </div>
  );
}

/** ⚠ Which way the office files. Every Revenue Department date in the system turns on this. */
function Channel({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [ch, setCh] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => { api.atChannel(clientId).then((d: any) => setCh(d.channel)).catch((e: any) => setErr(e.message)); }, [clientId]);
  const set = async (v: string) => {
    setErr('');
    try { await api.atSetChannel(clientId, v); setCh(v); } catch (e: any) { setErr(e.message); }
  };
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-secondary)' }}>
      <span style={{ fontWeight: 700 }}>{t('pa_tax_channel_label')}</span>
      {(['online', 'paper'] as const).map(v => (
        <button key={v} onClick={() => set(v)} style={{ ...btn, background: ch === v ? 'var(--accent)' : 'var(--bg-surface)', color: ch === v ? '#fff' : 'var(--text-primary)', border: ch === v ? 'none' : btn.border }}>
          {v === 'online' ? t('pa_tax_channel_online') : t('pa_tax_channel_paper')}
        </button>
      ))}
      <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
        {ch === 'paper'
          ? t('pa_tax_channel_deadlines_paper')
          : t('pa_tax_channel_deadlines_online')}
      </span>
      {err && <span style={{ fontSize: 12, color: '#B42318' }}>{err}</span>}
    </div>
  );
}

/** s.65 bis (5) — the locked method, the rates, the balances, and the draft entry. */
function Fx({ clientId, periodId, closed, onChanged }: { clientId: string; periodId: string; closed: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [m, setM] = useState<any>(null);
  const [pv, setPv] = useState<any>(null);
  const [chart, setChart] = useState<any[]>([]);
  const [f, setF] = useState({ accountId: '', currency: 'USD', amountFx: '', bookedSatang: '' });
  const [imp, setImp] = useState({ rateDate: '', currency: 'USD', kind: 'bot_average', rate: '', source: '' });
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, b, c]: any[] = await Promise.all([api.atFxMethod(clientId), api.atFxPreview(clientId, periodId), api.acctChart(clientId)]);
      setM(a); setPv(b); setChart((c.accounts || []).filter((x: any) => ['asset', 'liability'].includes(String(x.class))));
    } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);

  const go = async (what: string, fn: () => Promise<any>) => {
    setBusy(what); setErr(''); setMsg('');
    try { const r = await fn(); await load(); return r; } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
        {t('pa_tax_fx_rule_1')}<b>{t('pa_tax_fx_rule_not')}</b>{t('pa_tax_fx_rule_2')}
      </div>

      <div style={row}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_tax_fx_method_label')}</span>
        <select value={m?.method || ''} onChange={e => go('method', () => api.atSetFxMethod(clientId, e.target.value, e.target.value === 'other_approved' ? window.prompt(t('pa_tax_fx_other_method_prompt')) || '' : ''))} style={inp}>
          <option value="">{t('pa_tax_fx_method_none')}</option>
          {(m?.methods || []).map((x: any) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        {m?.note && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{m.note}</span>}
      </div>
      {m && !m.method && <div style={amber}>{t('pa_tax_fx_no_method_warn')}</div>}

      <div style={{ ...row, alignItems: 'flex-end' }}>
        <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_tax_fx_rate_label')}<br />
          <input type="date" value={imp.rateDate} onChange={e => setImp({ ...imp, rateDate: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <input placeholder="USD" value={imp.currency} onChange={e => setImp({ ...imp, currency: e.target.value.toUpperCase() })} style={{ ...inp, width: 70 }} />
        <select value={imp.kind} onChange={e => setImp({ ...imp, kind: e.target.value })} style={inp}>
          <option value="bot_average">{t('pa_tax_fx_kind_avg')}</option><option value="bot_buying">{t('pa_tax_fx_kind_buy')}</option><option value="bot_selling">{t('pa_tax_fx_kind_sell')}</option>
        </select>
        <input placeholder="34.5432" value={imp.rate} onChange={e => setImp({ ...imp, rate: e.target.value })} style={{ ...inp, width: 100 }} />
        <input placeholder={t('pa_tax_fx_source_ph')} value={imp.source} onChange={e => setImp({ ...imp, source: e.target.value })} style={{ ...inp, width: 200 }} />
        <button disabled={busy === 'rate'} style={btn} onClick={() => go('rate', async () => {
          const r: any = await api.atImportFxRates([{ rateDate: imp.rateDate, currency: imp.currency, kind: imp.kind, rate: Number(imp.rate) }], imp.source);
          setMsg(r.imported ? t('pa_tax_fx_rate_stored') : (r.skipped?.[0]?.why || t('pa_tax_fx_nothing_imported')));
          setImp({ ...imp, rate: '' });
        })}>
          <i className="ti ti-plus" aria-hidden /> {t('pa_tax_fx_add_rate')}
        </button>
      </div>

      {!closed && (
        <div style={row}>
          <select value={f.accountId} onChange={e => setF({ ...f, accountId: e.target.value })} style={{ ...inp, minWidth: 200 }}>
            <option value="">{t('pa_tax_fx_account_none')}</option>
            {chart.map(a => <option key={a.id} value={a.id}>{a.code} {a.name_th || a.name_en}</option>)}
          </select>
          <input placeholder="USD" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value.toUpperCase() })} style={{ ...inp, width: 70 }} />
          <input placeholder={t('pa_tax_fx_amount_fx_ph')} value={f.amountFx} onChange={e => setF({ ...f, amountFx: e.target.value })} style={{ ...inp, width: 150 }} />
          <input placeholder={t('pa_tax_fx_booked_ph')} value={f.bookedSatang} onChange={e => setF({ ...f, bookedSatang: e.target.value })} style={{ ...inp, width: 150 }} />
          <button disabled={busy === 'pos'} style={btn} onClick={() => go('pos', async () => {
            await api.atAddFxPosition(clientId, periodId, { accountId: f.accountId, currency: f.currency, amountFx: toSatang(f.amountFx), bookedSatang: toSatang(f.bookedSatang) });
            setF({ ...f, amountFx: '', bookedSatang: '' });
          })}><i className="ti ti-plus" aria-hidden /> {t('pa_tax_fx_add_balance')}</button>
        </div>
      )}

      {pv?.rows?.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('pa_tax_fx_th_account')}</th><th style={th}>{t('pa_tax_fx_th_cur')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_fx_th_amount')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_fx_th_booked')}</th><th style={th}>{t('pa_tax_fx_th_rate')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_fx_th_at_rate')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_fx_th_diff')}</th>
          </tr></thead>
          <tbody>
            {pv.rows.map((r: any) => (
              <tr key={r.positionId}>
                <td style={td}>{r.code} {r.name}</td>
                <td style={td}>{r.currency}</td>
                <td style={num}>{baht(r.amountFx)}</td>
                <td style={num}>{baht(r.bookedSatang)}</td>
                <td style={{ ...td, fontSize: 11.5, color: r.rateE6 ? 'var(--text-secondary)' : '#92400E' }}>
                  {r.rateE6 ? `${(r.rateE6 / 1e6).toFixed(6)} · ${r.rateKind.replace('bot_', '')} ${dmy(r.rateDate)}` : r.why}
                </td>
                <td style={num}>{r.bahtAtRate == null ? '—' : baht(r.bahtAtRate)}</td>
                <td style={{ ...num, color: r.diffSatang == null ? 'var(--text-tertiary)' : (r.diffSatang >= 0 ? '#166534' : '#B42318') }}>
                  {r.diffSatang == null ? '—' : baht(r.diffSatang)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_tax_fx_empty')}</div>}

      {(pv?.blocked || []).map((b: string, i: number) => <div key={i} style={amber}>{b}</div>)}
      {pv?.ready && !closed && (
        <div>
          <button disabled={busy === 'rev'} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}
            onClick={() => go('rev', async () => { const r: any = await api.atFxRevalue(clientId, periodId); setMsg(t('pa_tax_fx_revalued_msg', { doc: r.docNo, amount: baht(r.netDiffSatang), n: r.rows })); onChanged(); })}>
            <i className="ti ti-refresh" aria-hidden /> {t('pa_tax_fx_revalue_btn', { amount: baht(pv.netDiffSatang) })}
          </button>
        </div>
      )}
      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB', padding: '8px 11px', borderRadius: 8 }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 11px', borderRadius: 8 }}>{err}</div>}
    </div>
  );
}

/** ภ.ง.ด.54 and ภ.พ.36. No rate catalogue on purpose — see the service header. */
function Remittances({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const now = new Date();
  const [form, setForm] = useState<'pnd54' | 'pp36'>('pnd54');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ payDate: '', payeeName: '', payeeCountry: '', incomeNature: '', base: '', rate: '', legalBasis: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { setD(await api.atRemittances(clientId, form, year, month)); } catch (e: any) { setErr(e.message); }
  }, [clientId, form, year, month]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true); setErr('');
    try {
      await api.atAddRemittance(clientId, {
        form, payDate: f.payDate, payeeName: f.payeeName, payeeCountry: f.payeeCountry,
        incomeNature: f.incomeNature, baseSatang: toSatang(f.base), rate: Number(f.rate), legalBasis: f.legalBasis,
      });
      setF({ ...f, payeeName: '', base: '', rate: '' });
      await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const s = d?.summary;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={amber}>
        {t('pa_tax_rem_warn_1')}<b>{t('pa_tax_rem_warn_norate')}</b>{t('pa_tax_rem_warn_2')}
      </div>
      <div style={row}>
        <select value={form} onChange={e => setForm(e.target.value as any)} style={inp}>
          {(d?.forms || [{ key: 'pnd54', label: 'ภ.ง.ด.54' }, { key: 'pp36', label: 'ภ.พ.36' }]).map((x: any) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, width: 90 }} />
        <select value={month} onChange={e => setMonth(Number(e.target.value))} style={inp}>
          {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
        </select>
        {s && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('pa_tax_rem_lines', { n: s.lines })} · {t('pa_tax_rem_base', { amount: baht(s.baseSatang) })} · {t('pa_tax_rem_tax')} <b>{baht(s.taxSatang)}</b> · {t('pa_tax_rem_due', { date: dmy(s.due) })} ({s.channel})
          {s.filed ? ` · ${t('pa_tax_rem_filed')}` : ''}
        </span>}
      </div>

      <div style={row}>
        <input type="date" value={f.payDate} onChange={e => setF({ ...f, payDate: e.target.value })} style={{ ...inp, width: 140 }} />
        <input placeholder={t('pa_tax_rem_payee_ph')} value={f.payeeName} onChange={e => setF({ ...f, payeeName: e.target.value })} style={{ ...inp, width: 170 }} />
        <input placeholder={t('pa_tax_rem_country_ph')} value={f.payeeCountry} onChange={e => setF({ ...f, payeeCountry: e.target.value })} style={{ ...inp, width: 80 }} />
        <input placeholder={t('pa_tax_rem_nature_ph')} value={f.incomeNature} onChange={e => setF({ ...f, incomeNature: e.target.value })} style={{ ...inp, width: 130 }} />
        <input placeholder={t('pa_tax_rem_base_ph')} value={f.base} onChange={e => setF({ ...f, base: e.target.value })} style={{ ...inp, width: 110 }} />
        <input placeholder={t('pa_tax_rem_rate_ph')} value={f.rate} onChange={e => setF({ ...f, rate: e.target.value })} style={{ ...inp, width: 70 }} />
        <input placeholder={t('pa_tax_rem_basis_ph')} value={f.legalBasis} onChange={e => setF({ ...f, legalBasis: e.target.value })} style={{ ...inp, width: 180 }} />
        <button onClick={add} disabled={busy} style={btn}><i className="ti ti-plus" aria-hidden /> {t('pa_tax_rem_add')}</button>
      </div>

      {(d?.lines || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('pa_tax_rem_th_paid')}</th><th style={th}>{t('pa_tax_rem_th_payee')}</th><th style={th}>{t('pa_tax_rem_th_for')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_rem_th_base')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_rem_th_rate')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_rem_th_tax')}</th><th style={th}>{t('pa_tax_rem_th_basis')}</th><th style={th} />
          </tr></thead>
          <tbody>
            {d.lines.map((l: any) => (
              <tr key={l.id}>
                <td style={td}>{dmy(l.pay_date)}</td>
                <td style={td}>{l.payee_name}{l.payee_country ? ` · ${l.payee_country}` : ''}</td>
                <td style={td}>{l.income_nature || '—'}</td>
                <td style={num}>{baht(l.base_satang)}{l.currency !== 'THB' ? <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}><br />{l.currency} {baht(l.amount_fx)} @ {(Number(l.fx_rate_e6) / 1e6).toFixed(6)}</span> : null}</td>
                <td style={num}>{Number(l.rate)}%</td>
                <td style={num}>{baht(l.tax_satang)}</td>
                <td style={{ ...td, fontSize: 11, color: l.legal_basis ? 'var(--text-tertiary)' : '#92400E' }}>{l.legal_basis || t('pa_tax_rem_not_recorded')}</td>
                <td style={td}>{!Number(l.filed) && (
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }}
                    onClick={async () => { try { await api.atDeleteRemittance(clientId, l.id); await load(); } catch (e: any) { setErr(e.message); } }}>
                    <i className="ti ti-trash" aria-hidden />
                  </button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_tax_rem_empty')}</div>}

      {(s?.notes || []).map((n: string, i: number) => <div key={i} style={amber}>{n}</div>)}
      {s?.lines > 0 && !s.filed && (
        <div style={row}>
          <button style={btn} onClick={async () => {
            const on = window.prompt(t('pa_tax_rem_filed_prompt'), s.due);
            if (on === null) return;
            try { await api.atRemittanceFiled(clientId, { form, year, month, remittedOn: on }); await load(); } catch (e: any) { setErr(e.message); }
          }}><i className="ti ti-check" aria-hidden /> {t('pa_tax_rem_mark_filed')}</button>
        </div>
      )}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 11px', borderRadius: 8 }}>{err}</div>}
    </div>
  );
}

/** Stamp duty — the seven schedule rates, the register, and what is not established. */
function Stamp({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [rates, setRates] = useState<any>(null);
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ code: 'lease', instrumentDate: '', partyName: '', description: '', base: '', pick: '', vatApplicable: false });
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, b]: any[] = await Promise.all([api.atStampRates(), api.atStampInstruments(clientId)]);
      setRates(a); setD(b);
    } catch (e: any) { setErr(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const picked = (rates?.rates || []).find((r: any) => r.code === f.code);
  const add = async () => {
    setErr('');
    try {
      await api.atAddStampInstrument(clientId, {
        code: f.code, instrumentDate: f.instrumentDate, partyName: f.partyName, description: f.description,
        baseSatang: toSatang(f.base), pick: f.pick || undefined, vatApplicable: f.vatApplicable,
      });
      setF({ ...f, partyName: '', description: '', base: '' });
      await load();
    } catch (e: any) { setErr(e.message); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
        {t('pa_tax_stamp_rule')}
      </div>

      <div style={row}>
        <select value={f.code} onChange={e => setF({ ...f, code: e.target.value, pick: '' })} style={{ ...inp, minWidth: 230 }}>
          {(rates?.rates || []).map((r: any) => <option key={r.code} value={r.code}>{r.label_th} · {r.label_en}</option>)}
        </select>
        <input type="date" value={f.instrumentDate} onChange={e => setF({ ...f, instrumentDate: e.target.value })} style={{ ...inp, width: 140 }} />
        <input placeholder={t('pa_tax_stamp_party_ph')} value={f.partyName} onChange={e => setF({ ...f, partyName: e.target.value })} style={{ ...inp, width: 160 }} />
        {picked?.rule === 'per_base' && <input placeholder={t('pa_tax_stamp_base_ph')} value={f.base} onChange={e => setF({ ...f, base: e.target.value })} style={{ ...inp, width: 160 }} />}
        {picked?.rule === 'fixed_pair' && (
          <select value={f.pick} onChange={e => setF({ ...f, pick: e.target.value })} style={inp}>
            <option value="">{t('pa_tax_stamp_pick_none')}</option>
            <option value="a">{t('pa_tax_baht_amount', { amount: Number(picked.fixed_a_satang) / 100 })}{Number(picked.fixed_basis_known) ? t('pa_tax_stamp_one_act') : ''}</option>
            <option value="b">{t('pa_tax_baht_amount', { amount: Number(picked.fixed_b_satang) / 100 })}{Number(picked.fixed_basis_known) ? t('pa_tax_stamp_several_acts') : ''}</option>
          </select>
        )}
        {f.code === 'receipt' && (
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 5, alignItems: 'center' }}>
            <input type="checkbox" checked={f.vatApplicable} onChange={e => setF({ ...f, vatApplicable: e.target.checked })} /> {t('pa_tax_stamp_vat_exempt')}
          </label>
        )}
        <button onClick={add} style={btn}><i className="ti ti-plus" aria-hidden /> {t('pa_tax_stamp_record')}</button>
      </div>

      {picked && (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
          {t('pa_tax_stamp_schedule_head')} {picked.schedule_item}{Number(picked.number_inferred) ? t('pa_tax_stamp_number_inferred') : ''}
          {picked.rule === 'band' ? t('pa_tax_stamp_band', { min: Number(picked.band_min_satang) / 100, max: Number(picked.band_max_satang) / 100 }) : ''}
          {picked.exempt_note ? ` · ${picked.exempt_note}` : ''}
        </div>
      )}

      {(d?.instruments || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('pa_tax_stamp_th_date')}</th><th style={th}>{t('pa_tax_stamp_th_instrument')}</th><th style={th}>{t('pa_tax_stamp_th_party')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_stamp_th_base')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_stamp_th_duty')}</th>
            <th style={th}>{t('pa_tax_stamp_th_stamped')}</th><th style={th} />
          </tr></thead>
          <tbody>
            {d.instruments.map((i: any) => {
              const r = (rates?.rates || []).find((x: any) => x.code === i.code);
              return (
                <tr key={i.id}>
                  <td style={td}>{dmy(i.instrument_date)}</td>
                  <td style={td}>{r?.label_en || i.code}</td>
                  <td style={td}>{i.party_name || '—'}</td>
                  <td style={num}>{i.base_satang ? baht(i.base_satang) : '—'}</td>
                  <td style={num}>{i.duty_satang == null ? <span style={{ color: '#92400E' }}>{t('pa_tax_stamp_not_established')}</span> : baht(i.duty_satang)}</td>
                  <td style={{ ...td, color: i.paid_on ? '#166534' : (Number(i.exempt) ? 'var(--text-tertiary)' : '#B42318') }}>
                    {Number(i.exempt) ? t('pa_tax_stamp_exempt') : (i.paid_on ? `${dmy(i.paid_on)} · ${i.method}${i.receipt_no ? ` · ${i.receipt_no}` : ''}` : t('pa_tax_stamp_unpaid'))}
                  </td>
                  <td style={td}>{!i.paid_on && !Number(i.exempt) && (
                    <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                      const method = window.prompt(t('pa_tax_stamp_method_prompt'), 'adhesive');
                      if (!method) return;
                      const paidOn = window.prompt(t('pa_tax_stamp_paid_on_prompt'), i.instrument_date);
                      if (!paidOn) return;
                      const duty = i.duty_satang == null ? window.prompt(t('pa_tax_stamp_duty_prompt')) : null;
                      const receiptNo = method === 'cash' ? (window.prompt(t('pa_tax_stamp_receipt_prompt')) || '') : '';
                      try {
                        await api.atMarkStamped(clientId, i.id, { method, paidOn, receiptNo, dutySatang: duty == null ? undefined : toSatang(duty) });
                        await load();
                      } catch (e: any) { setErr(e.message); }
                    }}>{t('pa_tax_stamp_mark_btn')}</button>
                  )}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_tax_stamp_empty')}</div>}

      {d?.unpaid?.rows?.length ? (
        <div style={amber}>
          <b>{t('pa_tax_stamp_unstamped_count', { n: d.unpaid.rows.length })}</b> {t('pa_tax_stamp_duty_established', { amount: baht(d.unpaid.totalDutySatang) })}
          {d.unpaid.unquantified ? t('pa_tax_stamp_unquantified', { n: d.unpaid.unquantified }) : ''}.
          <br />{d.unpaid.surcharge.note}
          <br />{d.unpaid.deadline.note}
        </div>
      ) : null}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 11px', borderRadius: 8 }}>{err}</div>}
    </div>
  );
}

/** The loss carry-forward register, with no expiry until the period is recorded. */
function Losses({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ fy: String(new Date().getFullYear() - 1), loss: '', note: '' });
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setD(await api.atLosses(clientId)); } catch (e: any) { setErr(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const reg = d?.register;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      {reg && <div style={reg.years == null ? amber : { fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>{reg.caveat}</div>}
      <div style={row}>
        <input placeholder={t('pa_tax_loss_year_ph')} value={f.fy} onChange={e => setF({ ...f, fy: e.target.value })} style={{ ...inp, width: 80 }} />
        <input placeholder={t('pa_tax_loss_amount_ph')} value={f.loss} onChange={e => setF({ ...f, loss: e.target.value })} style={{ ...inp, width: 140 }} />
        <input placeholder={t('pa_tax_loss_note_ph')} value={f.note} onChange={e => setF({ ...f, note: e.target.value })} style={{ ...inp, width: 180 }} />
        <button style={btn} onClick={async () => {
          setErr('');
          try { await api.atAddLoss(clientId, { fy: Number(f.fy), lossSatang: toSatang(f.loss), note: f.note }); setF({ ...f, loss: '', note: '' }); await load(); }
          catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_tax_loss_record')}</button>
        {reg?.years == null && (
          <button style={btn} onClick={async () => {
            const years = window.prompt(t('pa_tax_loss_years_prompt'));
            if (!years) return;
            const basis = window.prompt(t('pa_tax_loss_basis_prompt'));
            if (!basis) return;
            setErr('');
            try { await api.atSetLossPeriod(Number(years), basis); await load(); } catch (e: any) { setErr(e.message); }
          }}><i className="ti ti-gavel" aria-hidden /> {t('pa_tax_loss_set_period')}</button>
        )}
      </div>

      {reg?.rows?.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('pa_tax_loss_th_year')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_loss_th_loss')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_loss_th_used')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_loss_th_left')}</th>
            <th style={th}>{t('pa_tax_loss_th_expires')}</th><th style={th}>{t('pa_tax_loss_th_used_in')}</th><th style={th} />
          </tr></thead>
          <tbody>
            {reg.rows.map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{r.fy}</td>
                <td style={num}>{baht(r.lossSatang)}</td>
                <td style={num}>{baht(r.usedSatang)}</td>
                <td style={num}><b>{baht(r.remainingSatang)}</b></td>
                <td style={{ ...td, color: r.expiresFy == null ? '#92400E' : 'var(--text-secondary)' }}>{r.expiry}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.uses.map((u: any) => `${u.usedFy}: ${baht(u.amountSatang)}`).join(' · ') || '—'}</td>
                <td style={td}>
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const usedFy = window.prompt(t('pa_tax_loss_use_year_prompt'), String(r.fy + 1));
                    if (!usedFy) return;
                    const amt = window.prompt(t('pa_tax_loss_use_amount_prompt'), String(r.remainingSatang / 100));
                    if (!amt) return;
                    setErr('');
                    try {
                      const x: any = await api.atUseLoss(clientId, r.id, { usedFy: Number(usedFy), amountSatang: toSatang(amt) });
                      if (x.expiredWarning) window.alert(x.expiredWarning);
                      await load();
                    } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_tax_loss_use_btn')}</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr>
            <td style={{ ...td, fontWeight: 800 }}>{t('pa_tax_loss_total')}</td>
            <td style={{ ...num, fontWeight: 800 }}>{baht(reg.totalLossSatang)}</td>
            <td style={{ ...num, fontWeight: 800 }}>{baht(reg.totalUsedSatang)}</td>
            <td style={{ ...num, fontWeight: 800 }}>{baht(reg.totalRemainingSatang)}</td>
            <td style={td} colSpan={3} />
          </tr></tfoot>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_tax_loss_empty')}</div>}
      {reg && !reg.reconciled && <div style={amber}>{t('pa_tax_loss_unreconciled')}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 11px', borderRadius: 8 }}>{err}</div>}
    </div>
  );
}

/** s.105 — money in with no receipt behind it. */
function Receipts({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const y = new Date().getFullYear();
  const [thr, setThr] = useState<any>(null);
  const [chk, setChk] = useState<any>(null);
  const [agg, setAgg] = useState<any>(null);
  const [err, setErr] = useState('');
  const [range, setRange] = useState({ from: `${y}-01-01`, to: `${y}-12-31` });

  const load = useCallback(async () => {
    setErr('');
    try {
      const a: any = await api.atReceiptThreshold(clientId);
      setThr(a);
      if (a.threshold) {
        try { setChk(await api.atReceiptCheck(clientId, range.from, range.to)); } catch (e: any) { setErr(e.message); setChk(null); }
        try { setAgg(await api.atDailyAggregate(clientId, range.from, range.to)); } catch { setAgg(null); }
      } else { setChk(null); setAgg(null); }
    } catch (e: any) { setErr(e.message); }
  }, [clientId, range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>{thr?.rule}</div>
      {thr && !thr.threshold && (
        <div style={amber}>
          {t('pa_tax_rc_no_threshold')}
        </div>
      )}
      <div style={row}>
        <button style={btn} onClick={async () => {
          const reg = window.prompt(t('pa_tax_rc_registrant_prompt'), thr?.threshold?.registrant || 'vat_sbt');
          if (!reg) return;
          const amt = window.prompt(t('pa_tax_rc_threshold_prompt', { ceiling: (reg === 'vat_sbt' ? 1000 : 10000) }), '0');
          if (amt === null) return;
          const notificationNo = window.prompt(t('pa_tax_rc_notification_prompt')) || '';
          setErr('');
          try { await api.atSetReceiptThreshold(clientId, { registrant: reg, thresholdSatang: toSatang(amt), notificationNo }); await load(); }
          catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-settings" aria-hidden /> {thr?.threshold ? t('pa_tax_rc_change_threshold') : t('pa_tax_rc_record_threshold')}</button>
        {thr?.threshold && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('pa_tax_baht_amount', { amount: baht(thr.threshold.satang) })} · {thr.threshold.registrant === 'vat_sbt' ? t('pa_tax_rc_registrant_yes') : t('pa_tax_rc_registrant_no')} · {t('pa_tax_rc_scope_level', { scope: thr.threshold.scope })}
          {thr.threshold.notificationNo ? ` · ${thr.threshold.notificationNo}` : ''}
        </span>}
        <input type="date" value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} style={{ ...inp, width: 140 }} />
        <input type="date" value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} style={{ ...inp, width: 140 }} />
      </div>

      {chk && (chk.rows.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_tax_rc_th_received')}</th><th style={th}>{t('pa_tax_rc_th_against')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_tax_rc_th_amount')}</th><th style={th}>{t('pa_tax_rc_th_desc')}</th></tr></thead>
          <tbody>
            {chk.rows.map((r: any) => (
              <tr key={r.txnId}>
                <td style={td}>{dmy(r.txnDate)}</td>
                <td style={td}>{r.docNo || r.docKind}</td>
                <td style={num}>{baht(r.amountSatang)}</td>
                <td style={{ ...td, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12.5, color: '#166534' }}>{t('pa_tax_rc_all_covered', { n: chk.checked })}</div>)}
      {(chk?.notes || []).map((n: string, i: number) => <div key={i} style={amber}>{n}</div>)}
      {agg?.days?.length ? (
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
          <b>{t('pa_tax_rc_daily_head')}</b> — {t('pa_tax_rc_daily_body', { n: agg.days.length, amount: baht(agg.totalSatang) })} {agg.note}
        </div>
      ) : null}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 11px', borderRadius: 8 }}>{err}</div>}
    </div>
  );
}

export default function TaxLedgers({ clientId, periodId, periodClosed, onChanged }:
  { clientId: string; periodId: string; periodClosed: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
        {t('pa_tax_title')}
      </div>
      <div style={{ padding: '10px 13px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
        <Channel clientId={clientId} />
      </div>
      <Section title={t('pa_tax_sec_fx_title')}
        subtitle={t('pa_tax_sec_fx_sub')}>
        <Fx clientId={clientId} periodId={periodId} closed={periodClosed} onChanged={onChanged} />
      </Section>
      <Section title={`${t('pa_tax_sec_rem_forms')} — ${t('pa_tax_sec_rem_title')}`}
        subtitle={t('pa_tax_sec_rem_sub')}>
        <Remittances clientId={clientId} />
      </Section>
      <Section title={t('pa_tax_sec_stamp_title')}
        subtitle={t('pa_tax_sec_stamp_sub')}>
        <Stamp clientId={clientId} />
      </Section>
      <Section title={t('pa_tax_sec_loss_title')}
        subtitle={t('pa_tax_sec_loss_sub')}>
        <Losses clientId={clientId} />
      </Section>
      <Section title={`${t('pa_tax_sec_rc_doc')} · ${t('pa_tax_sec_rc_title')}`}
        subtitle={t('pa_tax_sec_rc_sub')}>
        <Receipts clientId={clientId} />
      </Section>
    </div>
  );
}
